import { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import styles from "./styles/financialChart.module.scss";
import axios from "../../../../utilities/axios";
import { ParametersGlobals } from "../../../../core/parametersGlobals/ParametersGlobals";
import { Button, CheckPicker, DateRangePicker, Divider, FormGroup, Panel, Notification, RadioGroup, Radio, Input } from "rsuite";
import { TrendingUp, Store, Users } from "lucide-react";
import dayjs from "dayjs";
import { ValueType } from "rsuite/lib/DateRangePicker";

interface FinancialChartProps {
    employees: { label: string; value: number }[];
    stores: { label: string; value: number }[];
    author?: number[];
}

export default function FinancialChart({ employees, stores, author }: FinancialChartProps) {
    const [data, setData] = useState<any>(null);
    const [state, setState] = useState<any>(null);
    const [storeChartState, setStoreChartState] = useState<any>(null);
    const [employeeChartState, setEmployeeChartState] = useState<any>(null);
    const [chartTotal, setChartTotal] = useState<number>(0);
    const [storeChartLoading, setStoreChartLoading] = useState<boolean>(false);
    const [employeeChartLoading, setEmployeeChartLoading] = useState<boolean>(false);
    const [showEmployeeChart, setShowEmployeeChart] = useState<boolean>(false);
    const [showStoreChart, setShowStoreChart] = useState<boolean>(false);
    const [showEmployeeDetails, setShowEmployeeDetails] = useState<boolean>(false);
    const [employeeDetails, setEmployeeDetails] = useState<any>(null);
    const [employeeDetailsLoading, setEmployeeDetailsLoading] = useState<boolean>(false);
    const [employeeDetailSelectedName, setEmployeeDetailSelectedName] = useState<string>("");
    const [period, setPeriod] = useState<ValueType | undefined>([
        dayjs().startOf('month').toDate(),
        dayjs().endOf('month').toDate(),
    ]);
    const [selectedErrors, setSelectedErrors] = useState<number[]>([]);
    const [errorsData, setErrorsData] = useState<any[]>([]);
    const [loadingButton, setLoadingButton] = useState<boolean>(false);

    useEffect(() => {
        if (data) {
            if (data.chartData && Array.isArray(data.chartData.series) && data.chartData.series.length > 0) {
                setState(data.chartData);
                setChartTotal(data.totalValue);
                setShowStoreChart(false);
                setShowEmployeeChart(false);
            } else {
                setState(null);
                setStoreChartState(null);
                setEmployeeChartState(null);
                setShowEmployeeChart(false);
                setShowStoreChart(false);
            }
        }
    }, [data]);

    useEffect(() => {
        if (data) {
            if (!data.chartData) return;

            // Constroi novos labels no formato "Erro - contagem"
            const formattedLabels = data.chartData.options.labels.map((label: string, index: number) => {
                const match = data.data?.find((item: any) => item.errorName === label);
                const count = match?.count ?? 0;
                return `${label}: ${count}`;
            });

            const updatedOptions = {
                ...data.chartData.options,
                labels: formattedLabels,
                chart: {
                    ...(data.chartData.options.chart || {}),
                    zoom: {
                        enabled: false,
                    },
                    events: {
                        dataPointSelection: function (event: any, chartContext: any, config: any) {
                            const index = config.dataPointIndex;
                            const errorName = data.chartData.options?.labels?.[index];

                            if (errorName) {
                                handleSearchStoreChart(errorName);
                            }
                        }
                    }
                },
                tooltip: {
                    y: {
                        formatter: function () {
                            return ``;
                        },
                        title: {
                            formatter: (seriesName: any) => seriesName,
                        },
                    }
                }
            };

            setState({
                ...data.chartData,
                options: updatedOptions
            });
        }
    }, [data]);

    useEffect(() => {
        try {
            fetchSelectErrorData()
        } catch (error) {
            console.log(error);
            Notification.error({ title: 'Erro', description: 'Erro ao buscar Lançamentos', placement: 'topEnd', close: true });
        }
    }, [])

    const fetchSelectErrorData = async () => {
        try {
            const response = await axios.get(`/api/v1/dashboard/chart/financial/custom/select-error-data`, {
                params: {
                    groupErrors: ParametersGlobals.defaultFinancialGroupErrorID
                }
            });

            const filteredErrors = response.data
                .filter((error: any) => error.error_active === true)
                .map((error: any) => ({
                    value: error.error_id,
                    label: error.error_name
                }));

            const defaultSelected = filteredErrors.map((err: any) => err.value);

            setErrorsData(filteredErrors);
            setSelectedErrors(defaultSelected);
        } catch (error) {
            console.log(error);
            Notification.error({ title: 'Erro', description: 'Erro ao buscar Lançamentos', placement: 'topEnd', close: true });
        }
    };

    const handleSearchStoreChart = async (clickedErrorName: string) => {
        try {
            setStoreChartLoading(true);
            setShowEmployeeChart(false);
            setShowStoreChart(false);
            setEmployeeDetails([]);
            setShowEmployeeDetails(false);

            const response = await axios.get(`/api/v1/dashboard/chart/financial/store`, {
                params: {
                    errorName: clickedErrorName,
                    groupErrors: ParametersGlobals.defaultFinancialGroupErrorID,
                    period: period,
                    selectedErrors: selectedErrors,
                    authorId: author
                }
            });

            const storeChartData = response.data.chartData;

            const updatedStoreChartOptions = {
                ...storeChartData.options,
                chart: {
                    ...(storeChartData.options.chart || {}),
                    zoom: {
                        enabled: false,
                    },
                    events: {
                        dataPointSelection: function (event: any, chartContext: any, config: any) {
                            const index = config.dataPointIndex;
                            const clickedStore = storeChartData.customData?.[index];

                            if (clickedStore) {
                                handleSearchEmployeeChart(clickedStore.id, clickedErrorName);
                            }
                        }
                    }
                }
            };

            setStoreChartState({
                ...storeChartData,
                options: updatedStoreChartOptions
            });
            setShowStoreChart(true);
        } catch (error) {
            console.log(error);
        } finally {
            setStoreChartLoading(false);
        }
    };

    const handleSearchEmployeeChart = async (clickedStoreId: number, clickedErrorName: string) => {
        try {
            setEmployeeChartLoading(true);
            setEmployeeDetails([]);
            setShowEmployeeDetails(false);

            const response = await axios.get(`/api/v1/dashboard/chart/financial/employee`, {
                params: {
                    errorName: clickedErrorName,
                    groupErrors: ParametersGlobals.defaultFinancialGroupErrorID,
                    period: period,
                    selectedErrors: selectedErrors,
                    storeId: clickedStoreId,
                    authorId: author
                }
            });

            const employeeChartData = response.data.chartData;

            const updatedEmployeeChartOptions = {
                ...employeeChartData.options,
                chart: {
                    ...(employeeChartData.options.chart || {}),
                    zoom: {
                        enabled: false,
                    },
                    events: {
                        dataPointSelection: function (event: any, chartContext: any, config: any) {
                            const index = config.dataPointIndex;
                            const clickedEmployee = employeeChartData.customData?.[index];

                            if (clickedEmployee) {
                                handleEmployeeDetails(clickedEmployee, clickedStoreId, clickedErrorName);
                            }
                        }
                    }
                }
            };

            setEmployeeChartState({
                ...employeeChartData,
                options: updatedEmployeeChartOptions
            });
        } catch (error) {
            console.log(error);
        } finally {
            setEmployeeChartLoading(false);
            setShowEmployeeChart(true);
        }
    };

    const handleEmployeeDetails = async (clickedEmployee: any, clickedStoreId: number, clickedErrorName: string) => {
        try {
            setEmployeeDetailsLoading(true);

            const response = await axios.get(`/api/v1/dashboard/chart/financial/employee/details`, {
                params: {
                    errorName: clickedErrorName,
                    groupErrors: ParametersGlobals.defaultFinancialGroupErrorID,
                    period: period,
                    selectedErrors: selectedErrors,
                    storeId: clickedStoreId,
                    employeeId: clickedEmployee.id,
                    authorId: author
                }
            });

            setEmployeeDetails(response.data);
            setEmployeeDetailSelectedName(clickedEmployee.name);
        } catch (error) {
            console.log(error);
        } finally {
            setShowEmployeeDetails(true);
            setEmployeeDetailsLoading(false);
        }
    };

    const fetchChartData = async () => {
        try {
            const { data } = await axios.get("/api/v1/dashboard/group_error_chart", {
                params: {
                    filterType: "",
                    store: [],
                    employee: [],
                    errorGroup: ParametersGlobals.defaultFinancialGroupErrorID,
                    error: selectedErrors,
                    periodRangeFilter: period
                }
            });

            setState(data?.chartData)
            setChartTotal(data?.totalValue)
            setData(data)
        } catch (error) {
            console.error(error);

            Notification.error({
                title: "Erro!",
                description: "Erro ao buscar as informações do gráfico financeiro.",
                duration: 3500,
                placement: "topEnd"
            });
        }
    };

    const handleFilterChart = async () => {
        try {
            setLoadingButton(true)
            await fetchChartData()
        } catch (error) {
            console.log(error)
        } finally {
            setLoadingButton(false)
        }
    }

    const handleClearFilters = () => {
        const allErrorIds = errorsData.map((err) => err.value);

        setPeriod([
            dayjs().startOf('month').toDate(),
            dayjs().endOf('month').toDate(),
        ]);
        setSelectedErrors(allErrorIds);

        setData(null);
        setState(null);
        setStoreChartState(null);
        setEmployeeChartState(null);

        setChartTotal(0);
        setShowStoreChart(false);
        setShowEmployeeChart(false);
        setShowEmployeeDetails(false);
        setEmployeeDetails(null);
        setEmployeeDetailSelectedName("");
    };

    return (
        <div className={styles.container}>
            <h3>Gráfico Financeiro</h3>

            <Panel
                bordered
                style={{ backgroundColor: '#f9f9f9', borderRadius: '10px' }}
            >
                <div className={styles.filters}>
                    <div className={styles.filterSection}>
                        <FormGroup>
                            <label>Período</label>
                            <DateRangePicker
                                placeholder="Selecione uma data"
                                format="DD/MM/YYYY"
                                ranges={[]}
                                value={period}
                                onChange={(value) => setPeriod(value)}
                                style={{ width: 250 }}
                            />
                            <span className={styles.informationText}>* Obrigatório</span>
                        </FormGroup>

                        <FormGroup>
                            <label>Grupo de Lançamentos</label>
                            <Input
                                value="FINANCEIRO"
                                style={{ width: 150 }}
                                disabled
                            />
                            <span className={styles.informationText}>
                                * Obrigatório
                            </span>
                        </FormGroup>

                        <FormGroup style={{ width: '100%' }}>
                            <label>Lançamentos</label>
                            <CheckPicker
                                data={errorsData}
                                placeholder="Selecione os Lançamentos..."
                                cleanable
                                searchable
                                value={selectedErrors}
                                onChange={(value) => setSelectedErrors(value)}
                            />
                            <span className={styles.informationText}>
                                * Se nenhum filtro for aplicado, todos os registros serão incluídos
                            </span>
                        </FormGroup>

                        <Button appearance="primary" color="cyan" onClick={handleClearFilters} style={{ width: 175 }}>
                            Limpar
                        </Button>

                        <Button appearance="primary" onClick={handleFilterChart} loading={loadingButton} style={{ width: 175 }}>
                            Filtrar
                        </Button>
                    </div>

                    {/* <div className={styles.filterSection}>
                        <FormGroup controlId="radioList">
                            <span>‎ </span>
                            <RadioGroup
                                name="radioList"
                                inline
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', marginBottom: '1.4rem' }}
                                value={filterType}
                                onChange={(value) => setFilterType(value)}
                            >
                                <Radio value="store">Loja</Radio>
                                <Radio value="employee">Funcionário</Radio>
                            </RadioGroup>
                        </FormGroup>

                        {filterType === 'employee' && (
                            <div className={styles.filterDiv}>
                                <span>Funcionários</span>
                                <CheckPicker
                                    data={employees}
                                    placeholder="Funcionários para filtro..."
                                    style={{ width: '100%' }}
                                    value={selectedEmployees}
                                    onChange={(value) => setSelectedEmployees(value)}
                                />
                                <span className={styles.informationText}>
                                    *Se nenhum filtro for aplicado, todos os registros serão incluídos
                                </span>
                            </div>
                        )}

                        {filterType === 'store' && (
                            <div className={styles.filterDiv}>
                                <span>Lojas</span>
                                <CheckPicker
                                    data={stores}
                                    placeholder="Lojas para filtro..."
                                    style={{ width: '100%' }}
                                    value={selectedStores}
                                    onChange={(value) => setSelectedStores(value)}
                                />
                                <span className={styles.informationText}>
                                    *Se nenhum filtro for aplicado, todos os registros serão incluídos
                                </span>
                            </div>
                        )}
                    </div> */}

                    {/* <div className={styles.buttonFilters}> */}

                    {/* </div> */}
                </div>

                <div className={styles.chartsContainer}>
                    {state ? (
                        <>
                            <div className={styles.headerSummary}>
                                <div className={styles.summaryCard}><TrendingUp size={20} /> Total: R$ {chartTotal.toFixed(2)}</div>
                                <div className={styles.summaryCard}><Store size={20} /> Lojas: {Array.isArray(storeChartState?.series) ? storeChartState.series.length : 0}</div>
                                <div className={styles.summaryCard}><Users size={20} /> Funcionários: {Array.isArray(employeeChartState?.series) ? employeeChartState.series.length : 0}</div>
                            </div>

                            <div className={styles.mainChartCard}>
                                <h4 className={styles.chartTitle}>Gráfico por Lançamentos</h4>
                                <ReactApexChart options={state.options} series={state.series} type="pie" height={500} />
                            </div>
                        </>
                    ) : (
                        <div className={styles.noDataContainer}>
                            <p className={styles.noDataText}>Nenhum dado encontrado</p>
                        </div>
                    )}

                    <div className={styles.secundaryChartsContainer}>
                        {showStoreChart && storeChartState && (
                            <div className={styles.secundaryChartCard}>
                                <h5 className={styles.chartTitle}>Por Loja</h5>
                                {storeChartLoading ? (
                                    <p className={styles.loadingText}>Carregando gráfico da loja...</p>
                                ) : storeChartState ? (
                                    <ReactApexChart options={storeChartState.options} series={storeChartState.series} type="pie" height={400} />
                                ) : null}
                            </div>
                        )}

                        {showEmployeeChart && employeeChartState && (
                            <div className={styles.secundaryChartCard}>
                                <h5 className={styles.chartTitle}>Por Funcionário</h5>
                                {employeeChartLoading ? (
                                    <p className={styles.loadingText}>Carregando gráfico de funcionários...</p>
                                ) : employeeChartState ? (
                                    <ReactApexChart options={employeeChartState.options} series={employeeChartState.series} type="pie" height={400} />
                                ) : null}
                            </div>
                        )}
                    </div>

                    {showEmployeeDetails && employeeDetails && employeeDetails.length > 0 && (
                        <div className={styles.employeeDetailsList}>
                            <Divider>Detalhes do funcionário: <strong>{employeeDetailSelectedName}</strong></Divider>

                            {employeeDetailsLoading ? (
                                <p className={styles.loadingText}>Carregando detalhes dos funcionários...</p>
                            ) : (
                                <ul className={styles.employeeDetailsListContainer}>
                                    {employeeDetails?.map((item: any, index: number) => (
                                        <li key={index} className={styles.employeeDetailsListItem}>
                                            <span className={styles.employeeDate}>
                                                {dayjs(item.infringement_control_date).format('DD/MM/YYYY')}
                                            </span>
                                            <span className={styles.employeeName}>{item.error_name}</span>
                                            <span className={styles.employeeValue}>
                                                {item.infringement_control_value
                                                    ? `R$ ${item.infringement_control_value.toFixed(2).replace('.', ',')}`
                                                    : ''}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            </Panel>
        </div>
    );
}
